import { useState, type ComponentProps, type ReactNode } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar-next';
import { IconButton } from '@atlaskit/button-v25-3/new';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import CrossIcon from '@atlaskit/icon/core/cross';
import Tag from '@atlaskit/tag-next/simple-tag';
import { token } from '@atlaskit/tokens';

import { JsmPlaygroundHeading } from '../components/jsm-playground-heading';

import { EngineeringAvatar } from './jsm-playground-engineering-avatar';
import {
	ENGINEERING_REQUEST_TYPE_LABELS,
	type EngineeringQueueRow,
} from './jsm-playground-engineering-data';
import {
	EngineeringPriorityIcon,
	EngineeringRequestTypeIcon,
	EngineeringSlaTag,
} from './jsm-playground-engineering-marks';
import { engineeringMessages as messages } from './jsm-playground-engineering-messages';

type IconButtonIcon = NonNullable<ComponentProps<typeof IconButton>['icon']>;

const CloseIcon: IconButtonIcon = () => <CrossIcon label="" size="medium" />;
const CollapseIcon: IconButtonIcon = () => <ChevronUpIcon label="" size="medium" />;
const ExpandIcon: IconButtonIcon = () => <ChevronDownIcon label="" size="medium" />;

type EngineeringDetailProps = {
	height?: number;
	issueKey: string;
	onClose: () => void;
	row: EngineeringQueueRow;
	showSlaLabel: boolean;
	statusControl: ReactNode;
};

const DetailPanel = ({
	height,
	issueKey,
	onClose,
	row,
	showSlaLabel,
	statusControl,
}: EngineeringDetailProps) => {
	const intl = useIntl();
	const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(true);

	return (
		<div
			aria-label={intl.formatMessage(messages.requestDetailsLabel, { key: issueKey })}
			css={panelStyles}
			data-testid="engineering-request-detail"
			role="group"
			style={height ? { height: `${height}px` } : undefined}
		>
			<div css={panelContentStyles}>
				<div css={panelHeaderStyles}>
					<span css={issueKeyStyles}>
						<EngineeringRequestTypeIcon size="medium" type={row.requestType} />
						{issueKey}
					</span>
					<span css={headerStatusStyles}>{statusControl}</span>
					<IconButton
						icon={CloseIcon}
						label={intl.formatMessage(messages.closeDetails)}
						onClick={onClose}
					/>
				</div>

				<JsmPlaygroundHeading level={2} size="large">
					{row.summary}
				</JsmPlaygroundHeading>

				<section css={cardStyles}>
					<div css={descriptionHeaderStyles}>
						<EngineeringAvatar name={row.reporter} />
						<span css={reporterStyles}>
							<FormattedMessage
								{...messages.reporterLine}
								values={{
									reporter: row.reporter,
									strong: (chunks) => <span css={mediumTextStyles}>{chunks}</span>,
									link: (chunks) => <span css={linkStyles}>{chunks}</span>,
								}}
							/>
						</span>
						<IconButton
							appearance="subtle"
							icon={isDescriptionExpanded ? CollapseIcon : ExpandIcon}
							label={intl.formatMessage(
								isDescriptionExpanded ? messages.collapseDescription : messages.expandDescription,
							)}
							onClick={() => setIsDescriptionExpanded((current) => !current)}
							spacing="compact"
						/>
					</div>
					{isDescriptionExpanded && (
						<div css={descriptionBodyStyles}>
							<strong css={sectionLabelStyles}>
								<FormattedMessage {...messages.description} />
							</strong>
							<span>
								<FormattedMessage
									{...messages.descriptionBody}
									values={{ summary: row.summary, tail: row.descriptionTail }}
								/>
							</span>
							{row.requestType === 'system' && (
								<div css={attributeRowStyles}>
									<span css={attributeLabelStyles}>
										<FormattedMessage {...messages.affectedService} />
									</span>
									<Tag text={intl.formatMessage(messages.checkoutApi)} />
								</div>
							)}
						</div>
					)}
				</section>

				<CollapsibleCard defaultExpanded={false} title={intl.formatMessage(messages.slas)}>
					<DetailRow label={intl.formatMessage(messages.firstResponse)}>
						<span css={fieldValueStyles}>
							<CheckCircleIcon color={token('color.icon.success')} label="" size="small" />
							<FormattedMessage {...messages.met} />
						</span>
					</DetailRow>
					<DetailRow label={intl.formatMessage(messages.resolution)}>
						<EngineeringSlaTag isLoading={!showSlaLabel} row={row} />
					</DetailRow>
				</CollapsibleCard>

				<CollapsibleCard title={intl.formatMessage(messages.details)}>
					<DetailRow label={intl.formatMessage(messages.assignee)}>
						<span css={fieldValueStyles}>
							{row.assignee ? (
								<EngineeringAvatar name={row.assignee} />
							) : (
								<Avatar name={intl.formatMessage(messages.unassigned)} size="small" />
							)}
							{row.assignee ?? intl.formatMessage(messages.unassigned)}
						</span>
					</DetailRow>
					<DetailRow label={intl.formatMessage(messages.requestType)}>
						<span css={fieldValueStyles}>
							<EngineeringRequestTypeIcon size="medium" type={row.requestType} />
							{ENGINEERING_REQUEST_TYPE_LABELS[row.requestType]}
						</span>
					</DetailRow>
					<DetailRow label={intl.formatMessage(messages.detailsPriority)}>
						<span css={fieldValueStyles}>
							<EngineeringPriorityIcon priority={row.priority} />
							{row.priority}
						</span>
					</DetailRow>
				</CollapsibleCard>

				<section css={activityStyles}>
					<strong css={activityHeadingStyles}>
						<FormattedMessage {...messages.activity} />
					</strong>
					<div css={composerStyles}>
						<span>
							<FormattedMessage {...messages.addInternalNote} />
						</span>
						<span css={composerDividerStyles}>/</span>
						<span>
							<FormattedMessage {...messages.replyToCustomer} />
						</span>
					</div>
					<ActivityItem
						action={
							row.assignee
								? intl.formatMessage(messages.assignedRequest, { assignee: row.assignee })
								: intl.formatMessage(messages.addedToTriage)
						}
						actor="Robi Lopez"
						timestamp={intl.formatMessage(messages.yesterdayAt, { time: '2:42pm' })}
					/>
					<ActivityItem
						action={intl.formatMessage(messages.raisedRequest)}
						actor={row.reporter}
						timestamp={intl.formatMessage(messages.yesterdayAt, { time: '2:35pm' })}
					/>
				</section>
			</div>
		</div>
	);
};

const DetailRow = ({ children, label }: { children: ReactNode; label: string }) => (
	<div css={detailRowStyles}>
		<span css={fieldLabelStyles}>{label}</span>
		{children}
	</div>
);

const ActivityItem = ({
	action,
	actor,
	timestamp,
}: {
	action: string;
	actor: string;
	timestamp: string;
}) => (
	<div css={activityItemStyles}>
		<EngineeringAvatar name={actor} />
		<div css={activityCopyStyles}>
			<span>
				<strong css={boldTextStyles}>{actor}</strong> {action}
			</span>
			<span css={timestampStyles}>{timestamp}</span>
		</div>
	</div>
);

const CollapsibleCard = ({
	children,
	defaultExpanded = true,
	title,
}: {
	children: ReactNode;
	defaultExpanded?: boolean;
	title: string;
}) => {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);
	return (
		<section css={collapsibleStyles}>
			<div css={collapsibleHeaderStyles}>
				<strong css={collapsibleTitleStyles}>{title}</strong>
				<IconButton
					appearance="subtle"
					icon={isExpanded ? CollapseIcon : ExpandIcon}
					label={title}
					onClick={() => setIsExpanded((current) => !current)}
					spacing="compact"
				/>
			</div>
			{isExpanded && <div css={collapsibleBodyStyles}>{children}</div>}
		</section>
	);
};

export const JsmPlaygroundEngineeringDetail = DetailPanel;

const panelStyles = css({
	boxSizing: 'border-box',
	width: '348px',
	flexShrink: 0,
	alignSelf: 'flex-start',
	// Clip inside the border so the content can't paint over it or scroll. The height comes
	// from the queue table, which keeps this edge level with the table and inside the card.
	overflow: 'hidden',
	maxHeight: '100%',
	padding: token('space.200'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface.sunken'),
});
const panelContentStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
});
const panelHeaderStyles = css({ display: 'flex', alignItems: 'center', gap: token('space.100') });
const issueKeyStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.100'),
	flexShrink: 0,
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});
const headerStatusStyles = css({
	position: 'relative',
	zIndex: 2,
	display: 'flex',
	flex: '1 1 auto',
	minWidth: 0,
	justifyContent: 'flex-end',
	// Dropdown menu does not expose a surface styling prop. Keep its radius aligned with the
	// queue status menu while it renders inline to escape the panel without losing theme context.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-testid="engineering-status--content"]': {
		borderRadius: token('radius.large'),
	},
});
const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.200'),
	padding: token('space.200'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});
const descriptionHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.150'),
});
const reporterStyles = css({ flex: '1 1 auto', minWidth: 0, font: token('font.body') });
const mediumTextStyles = css({ fontWeight: token('font.weight.medium') });
const linkStyles = css({ color: token('color.link') });
const descriptionBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	font: token('font.body'),
});
const sectionLabelStyles = css({
	color: token('color.text.subtle'),
	fontWeight: token('font.weight.bold'),
});
const boldTextStyles = css({ fontWeight: token('font.weight.bold') });
const attributeRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	paddingBlockStart: token('space.050'),
});
const attributeLabelStyles = css({
	width: '112px',
	flexShrink: 0,
	color: token('color.text.subtle'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});
const detailRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
	minHeight: token('space.400'),
	paddingBlock: token('space.050'),
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.100'),
});
const fieldLabelStyles = css({
	width: '104px',
	flexShrink: 0,
	color: token('color.text'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});
const fieldValueStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.075'),
	minWidth: 0,
	font: token('font.body'),
});
const collapsibleStyles = css({
	overflow: 'hidden',
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});
const collapsibleHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	minHeight: token('space.500'),
	paddingInline: token('space.200'),
});
const collapsibleTitleStyles = css({ font: token('font.heading.xsmall') });
const collapsibleBodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	paddingBlock: token('space.100'),
	borderBlockStartWidth: token('border.width', '1px'),
	borderBlockStartStyle: 'solid',
	borderBlockStartColor: token('color.border'),
});
const activityStyles = css({ display: 'flex', flexDirection: 'column', gap: token('space.150') });
const activityHeadingStyles = css({ font: token('font.heading.xsmall') });
const composerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	gap: token('space.050'),
	paddingBlock: token('space.100'),
	paddingInline: token('space.050'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
});
const composerDividerStyles = css({ color: token('color.text.subtle') });
const activityItemStyles = css({
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.100'),
	padding: token('space.100'),
	borderWidth: token('border.width', '1px'),
	borderStyle: 'solid',
	borderColor: token('color.border'),
	borderRadius: token('radius.large'),
	backgroundColor: token('elevation.surface'),
});
const activityCopyStyles = css({
	display: 'flex',
	minWidth: 0,
	flexDirection: 'column',
	gap: token('space.050'),
	font: token('font.body'),
});
const timestampStyles = css({
	color: token('color.text.subtlest'),
	font: token('font.body.small'),
});
